import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface FranchisePartner {
  name: string
  fp_code: string
  system: string
  display_name: string
}

interface FranchiseContextType {
  selectedFranchise: FranchisePartner | null
  setSelectedFranchise: (franchise: FranchisePartner | null) => void
  fpCode: string
  setFpCode: (code: string) => void
}

const FranchiseContext = createContext<FranchiseContextType | undefined>(undefined)

export function FranchiseProvider({ children }: { children: ReactNode }) {
  const [selectedFranchise, setSelectedFranchiseState] = useState<FranchisePartner | null>(() => {
    // Load from session storage on mount
    const stored = sessionStorage.getItem('selectedFranchise')
    return stored ? JSON.parse(stored) : null
  })
  
  const [fpCode, setFpCodeState] = useState<string>(() => {
    // Load fp_code from session storage
    const stored = sessionStorage.getItem('fpCode')
    return stored || ''
  })

  // Save to session storage whenever selection changes
  const setSelectedFranchise = (franchise: FranchisePartner | null) => {
    setSelectedFranchiseState(franchise)
    if (franchise) {
      sessionStorage.setItem('selectedFranchise', JSON.stringify(franchise))
      sessionStorage.setItem('fpCode', franchise.fp_code)
      setFpCodeState(franchise.fp_code)
    } else {
      sessionStorage.removeItem('selectedFranchise')
      sessionStorage.removeItem('fpCode')
      setFpCodeState('')
    }
  }

  const setFpCode = (code: string) => {
    setFpCodeState(code)
    if (code) {
      sessionStorage.setItem('fpCode', code)
    } else {
      sessionStorage.removeItem('fpCode')
    }
  }

  return (
    <FranchiseContext.Provider value={{ 
      selectedFranchise, 
      setSelectedFranchise,
      fpCode,
      setFpCode
    }}>
      {children}
    </FranchiseContext.Provider>
  )
}

export function useFranchise() {
  const context = useContext(FranchiseContext)
  if (context === undefined) {
    throw new Error('useFranchise must be used within a FranchiseProvider')
  }
  return context
}